import React, { useState } from "react";

interface ActionBarProps {
  selectedCount: number;
  failedCount: number;
  hasSelection: boolean;
  pendingCount: number;
  saving: boolean;
  onCarryForward: () => void;
  onManualEntry: (price: string) => void;
  onSelectAllFailed: () => void;
  onClearSelection: () => void;
  onSave: () => void;
  onDiscard: () => void;
}

const ActionBar: React.FC<ActionBarProps> = ({
  selectedCount,
  failedCount,
  hasSelection,
  pendingCount,
  saving,
  onCarryForward,
  onManualEntry,
  onSelectAllFailed,
  onClearSelection,
  onSave,
  onDiscard,
}) => {
  const [manualPrice, setManualPrice] = useState("");

  const handleManualSubmit = () => {
    const trimmed = manualPrice.trim();
    if (!trimmed || Number.isNaN(Number(trimmed))) return;
    onManualEntry(trimmed);
    setManualPrice("");
  };

  const btnCls =
    "px-3 py-1.5 text-xs font-semibold rounded-md transition-colors disabled:opacity-40";
  const primaryBtn = `${btnCls} bg-rp-amber hover:bg-yellow-600 text-white`;
  const secondaryBtn =
    `${btnCls} bg-white dark:bg-rp-surface border border-gray-200 dark:border-rp-separator ` +
    "text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600";

  return (
    <div
      data-testid="action-bar"
      className="flex flex-wrap items-center gap-2 px-4 py-2 border-b border-gray-200 dark:border-rp-separator"
    >
      <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">
        {selectedCount > 0
          ? `${selectedCount} selected`
          : `${failedCount} failed slot${failedCount !== 1 ? "s" : ""}`}
      </span>

      <button
        type="button"
        className={secondaryBtn}
        onClick={onSelectAllFailed}
        disabled={failedCount === 0}
      >
        Select failed
      </button>

      {hasSelection && (
        <button type="button" className={secondaryBtn} onClick={onClearSelection}>
          Clear
        </button>
      )}

      <div className="flex items-center gap-1">
        <button
          type="button"
          className={primaryBtn}
          disabled={!hasSelection}
          onClick={onCarryForward}
        >
          Carry forward
        </button>

        <input
          type="text"
          placeholder="Price"
          aria-label="Manual override price"
          value={manualPrice}
          onChange={(e) => setManualPrice(e.target.value)}
          className={
            "w-20 px-2 py-1.5 text-xs font-mono bg-white dark:bg-rp-surface-raised border border-gray-200 " +
            "dark:border-rp-separator rounded-md text-gray-900 dark:text-white " +
            "focus:outline-none focus:border-rp-amber focus:ring-1 focus:ring-rp-amber transition-all"
          }
        />
        <button
          type="button"
          className={primaryBtn}
          disabled={!hasSelection || !manualPrice.trim()}
          onClick={handleManualSubmit}
        >
          Manual
        </button>
      </div>

      {pendingCount > 0 && (
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs font-semibold text-green-600 dark:text-green-400">
            {pendingCount} pending
          </span>
          <button
            type="button"
            className={secondaryBtn}
            onClick={onDiscard}
            disabled={saving}
          >
            Discard
          </button>
          <button
            type="button"
            className={`${btnCls} bg-green-600 hover:bg-green-700 text-white`}
            onClick={onSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save overrides"}
          </button>
        </div>
      )}
    </div>
  );
};

export default ActionBar;
